import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  loadRecruiterInbox,
  loadRecruiterConversation,
  upsertChatMessage,
  upsertInboxItem,
  setChatPresence,
  applyReadReceipt,
  setChatConversation,
  setChatMessages,
  markRecruiterRead,
  refreshRecruiterConversation,
  loadUnreadSummary,
  loadTeamInbox,
  appendInboxItems,
} from '../../Redux/ActionCreators/chatAction';
import messengerSocket from '../../utils/messengerSocket';
import RecruiterChat from './RecruiterChat';
import TeamChat from './TeamChat';
import './MessengerPage.scss';

const AVATAR_COLORS = {
  A: '#F75891', B: '#4A90D9', C: '#7B68EE', D: '#FF6B6B',
  E: '#00BCD4', F: '#F4A261', G: '#2D9E6B', H: '#E76F51',
  I: '#8338EC', J: '#3A86FF', K: '#06D6A0', L: '#E6A817',
  M: '#E63946', N: '#4361EE', O: '#F77F00', P: '#9C27B0',
  Q: '#0077B6', R: '#D62828', S: '#0288D1', T: '#558B2F',
  U: '#AD1457', V: '#1B6B45', W: '#6D6875', X: '#B5179E',
  Y: '#7209B7', Z: '#560BAD',
};

const getAvatarColor = (name) => {
  if (!name) return '#8a9199';
  const letter = name.trim().charAt(0).toUpperCase();
  return AVATAR_COLORS[letter] || '#8a9199';
};

const INBOX_LOAD_MORE_THRESHOLD_PX = 80;

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const getInitials = (name) => {
  if (!name) return 'C';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

const formatInboxTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const MessengerPage = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const inbox = useSelector((state) => state.chat.inbox);
  const conversation = useSelector((state) => state.chat.conversation);
  const teamTotalUnread = useSelector((state) => state.chat.teamTotalUnread);

  const [activeTab, setActiveTab] = useState('candidates'); // 'candidates' | 'team'
  const [activeJcId, setActiveJcId] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [mobileView, setMobileView] = useState('inbox');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [inboxLoadingMore, setInboxLoadingMore] = useState(false);
  const [inboxLoadMoreFailed, setInboxLoadMoreFailed] = useState(false);

  const activeJcIdRef = useRef(null);
  const conversationIdRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const inboxPageRef = useRef(1);
  const inboxHasMoreRef = useRef(false);
  const inboxSearchRef = useRef('');
  const inboxLoadingMoreRef = useRef(false);
  // Bumped on every reload from page 1 so in-flight "load more" results are dropped
  const inboxRequestIdRef = useRef(0);

  // Team group currently joined for the open team conversation
  const teamGroupRef = useRef(null);

  // Shared WS proxy object so TeamChat can register its handler
  const wsBridge = useRef({
    teamHandler: null,
    joinTeamGroup: (convId) => {
      const group = `internal-conversation-${convId}`;
      if (group === teamGroupRef.current) return;
      if (teamGroupRef.current) messengerSocket.leaveGroup(teamGroupRef.current);
      teamGroupRef.current = group;
      messengerSocket.joinGroup(group);
    },
  });

  useEffect(() => { activeJcIdRef.current = activeJcId; }, [activeJcId]);
  useEffect(() => { conversationIdRef.current = conversation?.conversation_id; }, [conversation?.conversation_id]);

  useEffect(() => {
    const init = async () => {
      setInboxLoading(true);
      await Promise.all([
        loadInboxFirstPage(),
        dispatch(loadUnreadSummary()),
        dispatch(loadTeamInbox()),
      ]);
      setInboxLoading(false);
    };
    init();

    // The socket itself lives in mainLayout (useMessengerSocket); groups joined
    // here are re-joined by messengerSocket after a reconnect.
    const offOpen = messengerSocket.addListener('open', async ({ reconnected }) => {
      if (!reconnected) return;
      await loadInboxFirstPage(inboxSearchRef.current);
      if (activeJcIdRef.current) {
        await dispatch(loadRecruiterConversation(activeJcIdRef.current));
      }
    });

    const offMessage = messengerSocket.addListener('message', (data) => {
      // Route team events to TeamChat's handler (the team inbox item itself is
      // upserted globally by useMessengerSocket)
      if (data.type && (
        data.type === 'internal_inbox_updated' ||
        data.type === 'internal_message' ||
        data.type === 'internal_message_read' ||
        data.type === 'internal_membership_updated'
      )) {
        wsBridge.current.teamHandler?.(data);
        return;
      }
      handleWsEvent(data);
    });

    return () => {
      offOpen();
      offMessage();
      // The open candidate's group is joined and left by RecruiterChat
      if (teamGroupRef.current) messengerSocket.leaveGroup(teamGroupRef.current);
      teamGroupRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const jcId = searchParams.get('jcId');
    if (jcId) selectConversation(jcId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // conversation_inbox_updated is handled globally by useMessengerSocket
  const handleWsEvent = (data) => {
    if (data.type === 'group_members') {
      if (data.group === `candidate-conversation-${conversationIdRef.current}`) {
        dispatch(setChatPresence(!!data.recruiter_online, !!data.candidate_online));
      }
      return;
    }

    if (data.type === 'conversation_message' && data.message) {
      const isActive = String(data.conversation_id) === String(conversationIdRef.current);
      if (isActive) {
        dispatch(upsertChatMessage(data.message));
        if (data.message.sender_type === 'candidate') {
          dispatch(markRecruiterRead(activeJcIdRef.current, data.message.id));
          dispatch(refreshRecruiterConversation(activeJcIdRef.current));
        }
      }
      return;
    }

    if (data.type === 'message_read') {
      if (String(data.conversation_id) === String(conversationIdRef.current)) {
        dispatch(applyReadReceipt('recruiter', data.last_read_message_id));
        dispatch(refreshRecruiterConversation(activeJcIdRef.current));
      }
    }
  };

  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  // ── Inbox pagination ──────────────────────────────────────────────
  const loadInboxFirstPage = async (search = '') => {
    const requestId = ++inboxRequestIdRef.current;
    inboxSearchRef.current = search;
    inboxPageRef.current = 1;
    inboxHasMoreRef.current = false;
    inboxLoadingMoreRef.current = false;
    setInboxLoadingMore(false);
    setInboxLoadMoreFailed(false);
    const result = await dispatch(loadRecruiterInbox(search, 1));
    if (requestId === inboxRequestIdRef.current) inboxHasMoreRef.current = !!result?.hasMore;
    return result;
  };

  const loadNextInboxPage = async () => {
    if (inboxLoadingMoreRef.current || !inboxHasMoreRef.current) return;
    const requestId = inboxRequestIdRef.current;
    const nextPage = inboxPageRef.current + 1;
    inboxLoadingMoreRef.current = true;
    setInboxLoadingMore(true);
    setInboxLoadMoreFailed(false);
    const result = await dispatch(loadRecruiterInbox(inboxSearchRef.current, nextPage));
    if (requestId !== inboxRequestIdRef.current) return; // list was reloaded meanwhile
    inboxLoadingMoreRef.current = false;
    setInboxLoadingMore(false);
    if (!result?.success) {
      // Stop auto-loading on scroll until the user hits Retry
      setInboxLoadMoreFailed(true);
      return;
    }
    dispatch(appendInboxItems(result.items));
    inboxPageRef.current = nextPage;
    inboxHasMoreRef.current = result.hasMore;
  };

  const handleInboxScroll = (e) => {
    if (inboxLoadMoreFailed) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < INBOX_LOAD_MORE_THRESHOLD_PX) loadNextInboxPage();
  };

  const unreadInboxCount = inbox.filter((item) => item.unread_count > 0).length;

  // Once every conversation has been read, drop the unread filter and show the full list
  useEffect(() => {
    if (unreadInboxCount === 0 && showUnreadOnly) setShowUnreadOnly(false);
  }, [unreadInboxCount, showUnreadOnly]);

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    clearTimeout(searchDebounceRef.current);
    loadInboxFirstPage();
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      loadInboxFirstPage(val.trim());
    }, 400);
  };

  const handleMobileBack = () => setMobileView('inbox');

  const selectConversation = async (jcId) => {
    if (jcId === activeJcIdRef.current) return;
    setActiveJcId(jcId);
    setMobileView('chat');
    dispatch(setChatConversation(null));
    dispatch(setChatMessages([]));
    setConvLoading(true);
    const result = await dispatch(loadRecruiterConversation(jcId));
    setConvLoading(false);
    if (result?.conversation) {
      const conv = result.conversation;
      const msgs = result.messages || [];
      const lastMsgId = msgs[0]?.id;
      dispatch(upsertInboxItem({
        job_candidate_id: jcId,
        candidate_name: conv.candidate_name,
        job_title: conv.job?.title || conv.job_title,
        unread_count: 0,
        last_message_at: conv.last_message_at,
        last_message_preview: conv.last_message_preview,
      }));
      if (lastMsgId) dispatch(markRecruiterRead(jcId, lastMsgId));
      dispatch(loadUnreadSummary());
    }
  };

  return (
    <div className="messenger-layout">
      {/* ── Tab bar ── */}
      <div className="messenger-tabs">
        <button
          className={`messenger-tab${activeTab === 'candidates' ? ' active' : ''}`}
          onClick={() => setActiveTab('candidates')}
        >
          Candidates
        </button>
        <button
          className={`messenger-tab${activeTab === 'team' ? ' active' : ''}`}
          onClick={() => setActiveTab('team')}
        >
          Team
          {teamTotalUnread > 0 && (
            <span className="messenger-tab-badge">{teamTotalUnread > 99 ? '99+' : teamTotalUnread}</span>
          )}
        </button>
      </div>

      {/* ── Candidates panel ── */}
      {activeTab === 'candidates' && (
        <div className="messenger-candidates-panel">
          <aside className={`messenger-sidebar${mobileView === 'inbox' ? ' mobile-open' : ''}`}>
            <div className="messenger-sidebar-header">
              <div className={`messenger-header-normal${searchOpen ? ' slide-out' : ''}`}>
                <span className="messenger-sidebar-title">Messages</span>
                {unreadInboxCount > 0 && (
                  <div className="messenger-unread-filter">
                    <button
                      className={`messenger-unread-filter-btn${!showUnreadOnly ? ' active' : ''}`}
                      onClick={() => setShowUnreadOnly(false)}
                    >
                      All
                    </button>
                    <button
                      className={`messenger-unread-filter-btn${showUnreadOnly ? ' active' : ''}`}
                      onClick={() => setShowUnreadOnly(true)}
                    >
                      Unread
                      <span className="messenger-unread-filter-count">{unreadInboxCount > 99 ? '99+' : unreadInboxCount}</span>
                    </button>
                  </div>
                )}
                <button className="messenger-search-icon-btn" onClick={openSearch} aria-label="Search conversations">
                  <SearchIcon />
                </button>
              </div>
              <div className={`messenger-header-search${searchOpen ? ' slide-in' : ''}`}>
                <SearchIcon />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="messenger-search-input"
                  placeholder="Search conversations…"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <button className="messenger-search-close-btn" onClick={closeSearch} aria-label="Close search">×</button>
              </div>
            </div>

            <div className="messenger-inbox-list" onScroll={handleInboxScroll}>
              {inboxLoading ? (
                <div className="messenger-inbox-loading">
                  <div className="spinner-border spinner-border-sm" role="status" />
                </div>
              ) : inbox.length === 0 ? (
                <p className="messenger-inbox-empty">No conversations yet</p>
              ) : (
                [...inbox]
                  // Keep the open conversation visible so it doesn't vanish once it's marked read
                  .filter((item) => !showUnreadOnly || item.unread_count > 0 || String(item.job_candidate_id) === activeJcId)
                  .sort((a, b) => {
                    if (!a.last_message_at && !b.last_message_at) return 0;
                    if (!a.last_message_at) return 1;
                    if (!b.last_message_at) return -1;
                    return new Date(b.last_message_at) - new Date(a.last_message_at);
                  })
                  .map((item) => {
                    const key = String(item.job_candidate_id);
                    const isActive = key === activeJcId;
                    return (
                      <div
                        key={key}
                        className={`messenger-inbox-item${isActive ? ' active' : ''}`}
                        onClick={() => selectConversation(key)}
                      >
                        <div className="messenger-inbox-avatar-wrap">
                          <div
                            className="messenger-inbox-avatar"
                            style={{ background: getAvatarColor(item.candidate_name) }}
                          >
                            {getInitials(item.candidate_name)}
                          </div>
                        </div>
                        <div className="messenger-inbox-body">
                          <div className="messenger-inbox-row-top">
                            <span className="messenger-inbox-name">{item.candidate_name || 'Candidate'}</span>
                            <span className="messenger-inbox-time">{formatInboxTime(item.last_message_at)}</span>
                          </div>
                          <div className="messenger-inbox-job" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <span>{item.job_title}</span>
                            {item.unread_count > 0 && (
                              <span className="messenger-total-unread messenger-total-unread-below" style={{ border: '2px solid #F75891' }}>
                                {item.unread_count > 99 ? '99+' : item.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
              )}
              {!inboxLoading && inboxLoadingMore && (
                <div className="messenger-inbox-loading">
                  <div className="spinner-border spinner-border-sm" role="status" />
                </div>
              )}
              {!inboxLoading && inboxLoadMoreFailed && (
                <p className="messenger-inbox-empty">
                  Couldn't load more conversations.{' '}
                  <button type="button" className="messenger-inbox-retry" onClick={loadNextInboxPage}>Retry</button>
                </p>
              )}
            </div>
          </aside>

          <main className={`messenger-main${mobileView === 'chat' ? ' mobile-chat' : ''}`}>
            {activeJcId && (
              <button className="messenger-mobile-back" onClick={handleMobileBack}>
                <span className="messenger-mobile-back-arrow">&#8592;</span>
                Messages
              </button>
            )}
            {activeJcId ? (
              <RecruiterChat
                jobCandidateId={activeJcId}
                candidateName={inbox.find((r) => String(r.job_candidate_id) === activeJcId)?.candidate_name}
                convLoading={convLoading}
              />
            ) : (
              <div className="messenger-empty-state">
                <div className="messenger-empty-icon">💬</div>
                <p>Select a conversation to start messaging</p>
              </div>
            )}
          </main>
        </div>
      )}

      {/* ── Team panel ── */}
      {activeTab === 'team' && (
        <div className="messenger-team-panel">
          <TeamChat wsRef={wsBridge.current} />
        </div>
      )}
    </div>
  );
};

export default MessengerPage;
